import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import { selectLineTemplate } from "../tool-registry"
import GeometryLayer, { GeometryMode } from "plugins/shared/layers/GeometryLayer.class"
import { convertHexStringToRGBArray } from "@nova/util-strings"
import { useEffect, useMemo, useRef } from "react"
import { TextureSource } from "@deck.gl/core"
import { arePolygonsEqual, calculateArea } from "@nova/calculate-geospatial"
import { convertLineWidthToValue } from "./module"
import { FillStyleExtension } from "@deck.gl/extensions"
import { polygonPatternMapping } from "./constants"
import { Line } from "@nova/protobuf"
import { decodeNumberToColor } from "@nova/serializer/src/client/util"
import { selectLinesToDraw } from "features/entity-caching/lines/selectors"
import { Geometry } from "plugins/shared/layers/types"
import { useNonReactiveCallback } from "@nova/use-non-reactive-callback"
import { GeometryType } from "@nova/constants"
import { clearEditingData, MOCKED_LINE_ID, selectLineEditingData } from "./LineSlice"
import { GeometryDataStore } from "plugins/shared/layers/GeometryDataStore"
import { createLine, deleteLine, updateLine } from "features/pubsub/entityActions/cubes/line"
import { selectUserID } from "user/profile/selectors"
import { getGlobalTerrainData } from "features/map/GlobalTerrainManager"

const unwrapLine = (d: Line | Geometry) => {
    if ("index" in d) return d as Line
    else return undefined
}

export const useNormalLineLayer = () => {
    const lines = useAppSelector(selectLinesToDraw)
    const editingData = useAppSelector(selectLineEditingData)
    const filteredLines = useMemo(() => {
        return lines
            .filter((e) => e.id !== editingData?.id)
            .map((e, i) => ({ ...e, deckGlIdx: i, idx: i }))
    }, [lines, editingData])

    const lineTemplate = useAppSelector(selectLineTemplate)
    const lineTemplateRef = useRef(lineTemplate)
    useEffect(() => {
        lineTemplateRef.current = lineTemplate
    }, [lineTemplate])

    if (filteredLines.length === 0) return []

    return new GeometryLayer({
        id: `line-geometry`,
        data: filteredLines,
        visible: true,
        extraLayers: [],
        styles: {
            points: {},
            selectedPoint: {},
            midpoints: {},
            lines: {
                widthScale: 1,
                widthMaxPixels: 8,
                widthMinPixels: 2,
                capRounded: false,
                billboard: false,
                getColor: (d: Line) => {
                    return d.color
                        ? convertHexStringToRGBArray(decodeNumberToColor(d.color))
                        : convertHexStringToRGBArray(decodeNumberToColor(lineTemplate.color))
                },
                getWidth: (d: Line) => {
                    const line = unwrapLine(d)
                    if (line) return convertLineWidthToValue(line)
                    else return convertLineWidthToValue(lineTemplate)
                },
                miterLimit: 0,
            },
            polygons: {
                stroked: true,
                getLineWidth: 3,
                lineWidthUnits: "pixels",
                lineMiterLimit: 0,
                getLineColor: (d: Line) => {
                    return d.outline
                        ? convertHexStringToRGBArray(decodeNumberToColor(d.outline))
                        : convertHexStringToRGBArray(decodeNumberToColor(lineTemplate.color))
                },
                getFillColor: (d: Line) => {
                    const color = d.fill && convertHexStringToRGBArray(decodeNumberToColor(d.fill))
                    if (!color)
                        return convertHexStringToRGBArray(decodeNumberToColor(lineTemplate.fill))
                    return color
                },
                fillPatternAtlas: "/images/plugins/polygon-pattern-map.png" as TextureSource,
                fillPatternMapping: polygonPatternMapping,
                getFillPattern: (d: Line) => {
                    return d.polygonPattern || "default"
                },
                getFillPatternScale: (d: Line) => {
                    if (!d.polygonPattern || d.polygonPattern === "default") return 1
                    const geospatialCoords = d.coordinates
                    //base the patterns size on the polygons area, then normalize it
                    const area = calculateArea(geospatialCoords)
                    const minScale = 0.2
                    const maxScale = 100
                    const scale =
                        ((area - minScale) / (7500000000 - minScale)) * (maxScale - minScale) +
                        minScale
                    return Math.max(Math.min(scale, 1000), 0.5)
                },
                getFillPatternOffset: [0, 0],
                extensions: [
                    ...getGlobalTerrainData().extensions,
                    new FillStyleExtension({ pattern: true }),
                ],
            },
        },
    })
}

export const useEditingLineLayer = () => {
    const dispatch = useAppDispatch()
    const userId = useAppSelector(selectUserID)!
    const editingData = useAppSelector(selectLineEditingData)
    const filteredLines = useMemo(() => {
        return editingData ? [{ ...editingData.mockedLine, deckGlIdx: 0 }] : []
    }, [editingData])

    const lineTemplate = useAppSelector(selectLineTemplate)
    const lineTemplateRef = useRef(lineTemplate)
    useEffect(() => {
        lineTemplateRef.current = lineTemplate
    }, [lineTemplate])

    const primaryColor =
        filteredLines[0]?.geometryType === GeometryType.Polygon
            ? decodeNumberToColor(lineTemplate.outline)
            : decodeNumberToColor(lineTemplate.color)
    const rgbaLineColor = convertHexStringToRGBArray(primaryColor)

    const flush = useNonReactiveCallback(
        (store: GeometryDataStore, save: boolean) => {
            if (save) {
                if (editingData?.id === MOCKED_LINE_ID && store.polygon.coordinates.length >= 2) {
                    const lineData = { ...lineTemplateRef.current }
                    if (store.polygon.geometryType === GeometryType.Polygon) {
                        lineData.outline = lineData.color
                    }
                    dispatch(
                        createLine({
                            data: {
                                coordinates: store.polygon.coordinates,
                                geometryType: store.polygon.geometryType,
                                ...lineData,
                                holes: [],
                                createdBy: { userId },
                            },
                        })
                    )
                } else if (editingData && editingData.id !== MOCKED_LINE_ID) {
                    const coordinates = store.polygon.coordinates

                    if (coordinates.length < 2) {
                        dispatch(deleteLine({ id: editingData.id }))
                    } else if (
                        editingData.mockedLine.geometryType !== store.polygon.geometryType ||
                        !arePolygonsEqual([editingData.mockedLine], [{ coordinates }])
                    ) {
                        dispatch(
                            updateLine({
                                id: editingData.id,
                                updates: { coordinates, geometryType: store.polygon.geometryType },
                            })
                        )
                    }
                }
            }
            dispatch(clearEditingData())
        },
        [editingData]
    )
    if (filteredLines.length === 0) return []

    return new GeometryLayer({
        id: `line-geometry-editing`,
        data: filteredLines,
        visible: true,
        editData: {
            canEdit: true,
            flush,
        },
        drawData: {
            canDraw: true,
            drawMode: "points",
        },
        extraLayers: [],
        geometryName: "Line",
        mode: GeometryMode.LineToPolygon,
        styles: {
            points: {
                filled: true,
                stroked: true,
                radiusMinPixels: 6,
                radiusMaxPixels: 6,
                getFillColor: [255, 255, 255],
                lineWidthUnits: "pixels",
                getLineColor: rgbaLineColor || [112, 131, 240],
            },
            selectedPoint: {
                filled: true,
                stroked: true,
                radiusMinPixels: 6,
                radiusMaxPixels: 6,
                getLineColor: [255, 255, 255],
                getLineWidth: 2,
                lineWidthUnits: "pixels",
                getFillColor: rgbaLineColor || [112, 131, 240],
            },
            midpoints: {
                filled: true,
                stroked: true,
                radiusMinPixels: 4,
                radiusMaxPixels: 4,
                getFillColor: [255, 255, 255, 255],
                getLineWidth: 2,
                lineWidthUnits: "pixels",
                getLineColor: rgbaLineColor || [112, 131, 240, 255],
            },
            lines: {
                widthScale: 1,
                widthMaxPixels: 8,
                widthMinPixels: 2,
                capRounded: false,
                billboard: false,
                getColor: rgbaLineColor,
                getWidth: (d: Line | Geometry) => {
                    const line = unwrapLine(d)
                    if (line) return convertLineWidthToValue(line)
                    else return convertLineWidthToValue(lineTemplate)
                },
                miterLimit: 0,
                extensions: [...getGlobalTerrainData().extensions],
            },
            polygons: {
                stroked: true,
                getLineWidth: 3,
                lineWidthUnits: "pixels",
                lineMiterLimit: 0,
                getLineColor: rgbaLineColor,
                getFillColor: convertHexStringToRGBArray(decodeNumberToColor(lineTemplate.fill)),
                fillPatternAtlas: "/images/plugins/polygon-pattern-map.png" as TextureSource,
                fillPatternMapping: polygonPatternMapping,
                getFillPattern: (d: Line) => {
                    return d.polygonPattern || "default"
                },
                getStroke: () => {
                    return lineTemplate.stroke
                },
                getFillPatternScale: (d: Line) => {
                    if (!d.polygonPattern || d.polygonPattern === "default") return 1
                    const geospatialCoords = d.coordinates
                    //base the patterns size on the polygons area, then normalize it
                    const area = calculateArea(geospatialCoords)
                    const minScale = 0.2
                    const maxScale = 100
                    const scale =
                        ((area - minScale) / (7500000000 - minScale)) * (maxScale - minScale) +
                        minScale
                    return Math.max(Math.min(scale, 1000), 0.5)
                },
                getFillPatternOffset: [0, 0],
                extensions: [
                    ...getGlobalTerrainData().extensions,
                    new FillStyleExtension({ pattern: true }),
                ],
            },
        },
    })
}
